<script lang="tsx">
import { BdForm } from '-/Base/BdForm'
import { DoubleRightOutlined } from '@ant-design/icons-vue'
import { createNamespace, deepClone, deepMerge } from '@bd/vue3-utils'
import { Button, message } from 'ant-design-vue'
import { computed, defineComponent, onMounted, onUnmounted, ref, nextTick, watch, toRaw } from 'vue'
import { conditionBoxProps } from './props'
const [name, bem] = createNamespace('ConditionBox')
export default defineComponent({
  name,
  props: conditionBoxProps,
  emits: ['change', 'expand'],
  setup(props, ctx) {
    // BdForm 实例引用：用于读取/校验/重置表单模型
    const formRef = ref<any>(null)
    // 表单容器引用：用于计算实际高度，判断是否需要“展开/收起”
    const formBoxRef = ref<any>(null)

    // 表单当前渲染高度（px）
    const formHeight = ref(0)

    // 重新计算表单实际高度
    const updateFormHeight = () => {
      formHeight.value = formBoxRef.value?.getBoundingClientRect?.()?.height ?? 0
    }
    const handleReset = () => {
      // formRef.value?.resetFields?.()
      const modelRef = formRef.value?.modelRef
      if (!modelRef) return
      for (const key in modelRef) {
        modelRef[key] = undefined
      }

      ctx.emit('change', deepClone(toRaw(modelRef)))
    }
    const handleConfirm = () => {
      formRef.value
        .validate()
        .then(() => {
          const modelRef = formRef.value?.modelRef
          ctx.emit('change', deepClone(toRaw(modelRef)))
        })
        .catch(err => {
          err.errorFields && message.error(err.errorFields[0].errors[0])
        })
    }
    // 可选：用 ResizeObserver 更可靠地感知内容高度变化（例如表单项异步渲染、折行等）
    let ro: ResizeObserver | null = null
    onMounted(() => {
      nextTick().then(updateFormHeight)

      // 监听窗口变化：避免页面缩放/布局变化导致高度判断不准确
      window.addEventListener('resize', updateFormHeight)

      const el = formBoxRef.value as HTMLElement | undefined
      if (el && typeof ResizeObserver !== 'undefined') {
        ro = new ResizeObserver(() => updateFormHeight())
        ro.observe(el)
      }
    })
    onUnmounted(() => {
      window.removeEventListener('resize', updateFormHeight)
      ro?.disconnect()
      ro = null
    })

    const showExpand = computed(() => formHeight.value > 47)
    const expanded = ref(true)
    /* dropdownMatchSelectWidth */
    const config = computed(
      () =>
        props.config?.map(item => {
          if (item.type && ['select', 'selects'].includes(item.type)) {
            return deepMerge(item, {
              attrs: { dropdownMatchSelectWidth: false, maxTagCount: 1 }
            })
          }
          return item
        })
    )
    const handleExpand = () => {
      expanded.value = !expanded.value
      ctx.emit('expand', expanded.value)
    }
    // 当 schema 变化时，下一帧重新计算高度（避免“展开”按钮显示不及时）
    watch(
      () => props.config,
      () => nextTick().then(updateFormHeight),
      { deep: true }
    )

    return () => (
      <div class={bem({ block: props.block })}>
        <div class={bem('wrap', { expanded: expanded.value })}>
          <div class={bem('form')} ref={formBoxRef}>
            <BdForm
              layout='horizontal'
              ref={formRef}
              attrs={{
                labelAlign: 'left',
                colon: false
              }}
              schema={config.value}
            />
          </div>
        </div>
        <div class={bem('btn')}>
          <Button size='middle' type='primary' onClick={handleConfirm}>
            查询
          </Button>
          <Button size='middle' onClick={handleReset}>
            重置
          </Button>
          {showExpand.value ? (
            <div class={bem('expand', { active: expanded.value })} onClick={handleExpand}>
              <span>{expanded.value ? '收起' : '展开'}</span>
              <DoubleRightOutlined />
            </div>
          ) : null}
        </div>
      </div>
    )
  }
})
</script>
<style lang="less">
.condition-box {
  display: flex;
  align-items: flex-end;
  background: @white;

  &--block {
    margin-bottom: 16px;
    padding: 15px 16px 0 16px;
    border-radius: @border-radius-base;
  }

  &__wrap {
    flex: 1;
    height: 47px;
    overflow: hidden;

    &--expanded {
      height: auto;
    }
  }

  .ant-select {
    min-width: 180px;
    max-width: 300px;
  }

  .ant-form > .ant-row {
    display: block;
  }

  .bd-form__item {
    display: inline-block;

    &:not(:last-child) {
      padding-right: 20px;
    }
  }

  .ant-form-item {
    margin-bottom: 15px;
  }

  &__btn {
    display: inline-flex;
    align-items: center;
    margin-bottom: 15px;

    & > *:not(:last-child) {
      margin-right: 10px;
    }
  }

  &__expand {
    .active(@primary-color);
    display: flex;
    align-items: center;
    height: 32px;
    font-size: 14px;
    line-height: 32px;
    white-space: nowrap;

    .anticon {
      margin-left: 5px;
      transition: transform 0.5s;
      transform: rotate(90deg);
    }

    &--active {
      .anticon {
        transform: rotate(-90deg);
      }
    }
  }
}
</style>
